<template>
  <div class="relative">
    <div class="w-full flex items-center border-b pb-4 mb-3">
      <div class="w-128 flex flex-col mr-5">
        <label class="mb-1 text-xs">Search:</label>
        <div class="w-full border flex items-center border-grey-dark rounded p-2">
          <icon name="magnify_glass" :center="false" class="text-grey-dark w-8 h-8" />
          <input class="w-full text-sm" placeholder="Postcode, Town, Village..." v-model="filtering.searchTerm" />
        </div>
      </div>

      <div class="mt-4 mr-5">
        <input type="radio" class="blue" id="sales" value="RS" v-model="filtering.department">
        <label for="sales">Sales</label>
      </div>

      <div class="mt-4 mr-10">
        <input type="radio" class="blue" id="lettings" value="RL" v-model="filtering.department">
        <label for="lettings">Lettings</label>
      </div>

      <div class="w-full flex">
        <div class="w-64 flex flex-col mr-4">
          <label class="mb-1 text-xs">Property Type:</label>
          <pl-select selectClass="w-full bg-white rounded border-grey-dark text-xs" v-model="filtering.propertyType">
            <optgroup v-for="(group, i) in propertyEnums.type" :key="i" :label="group.label">
              <option v-for="(type, j) in group.options" :key="j" :value="type.value">{{ type.label }}</option>
            </optgroup>
          </pl-select>
        </div>

        <div class="w-32 flex flex-col mr-4">
          <label class="mb-1 text-xs">Min {{ filtering.department === 'RS' ? 'Price' : 'Rent (PCM)' }}:</label>
          <pl-select selectClass="bg-white rounded border-grey-dark text-xs" v-model="filtering.min_price">
            <option :value="null">No Min</option>
            <option v-for="price in prices" :value="price" :key="price">{{ price.toLocaleString() }}</option>
          </pl-select>
        </div>

        <div class="w-32 flex flex-col mr-4">
          <label class="mb-1 text-xs">Max {{ filtering.department === 'RS' ? 'Price' : 'Rent (PCM)' }}:</label>
          <pl-select selectClass="bg-white rounded border-grey-dark text-xs" v-model="filtering.max_price">
            <option :value="null">No Max</option>
            <option v-for="price in prices" :value="price" :key="price">{{ price.toLocaleString() }}</option>
          </pl-select>
        </div>

        <div class="w-24 flex flex-col">
          <label class="mb-1 text-xs">Min Beds:</label>
          <pl-select selectClass="bg-white rounded border-grey-dark text-xs" v-model="filtering.min_bedrooms">
            <option :value="null">No Min</option>
            <option v-for="i in 10" :value="i" :key="i">{{ i }}</option>
          </pl-select>
        </div>
      </div>
    </div>

    <div class="w-full h-full flex flex-col items-center">
      <table class="w-full">
        <thead class="border-b">
          <tr>
            <td class="py-6 text-xs font-semibold uppercase text-left">Property ID</td>
            <td class="py-6 text-xs font-semibold uppercase text-left">Address</td>
            <td class="py-6 text-xs font-semibold uppercase text-center">Price</td>
            <td class="py-6 text-xs font-semibold uppercase text-center">Listed</td>
            <td class="py-6 text-xs font-semibold uppercase text-center">Beds</td>
            <td class="py-6 text-xs font-semibold uppercase text-center">Include</td>
          </tr>
        </thead>
        <tbody v-if="loading && properties.length === 0">
          <tr>
            <td class="py-6 text-xs text-center" colspan="8">
              <loader />
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="properties.length === 0">
          <tr>
            <td class="py-6 text-xs text-center" colspan="8">No Results</td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr v-for="property in properties" :key="property.id" class="border-b">
            <td class="py-6 text-sm uppercase">
              <div class="flex items-center">
                <div
                  class="w-16 h-10 bg-cover mr-3"
                  :style="{ 'background-image': `url(${ property.main_photo })`}"
                />
                <span class="text-grey">{{ property.id }}</span>
              </div>
            </td>
            <td class="py-6 text-sm">
              <div class="flex flex-col">
                <span class="w-48">{{ property.address }}</span>
              </div>
            </td>
            <td class="w-32 py-6 text-sm text-center font-semibold">
              {{ property.price }}
            </td>
            <td class="py-6 text-sm text-center">
              <div class="flex flex-col">
                <span class="text-xs mb-1">{{ property.uploaded_at_date_time.date }}</span>
                <span class="text-xs">{{ property.uploaded_at_date_time.time }}</span>
              </div>
            </td>
            <td class="py-6 text-sm text-center font-bold">
              {{ property.bedrooms }}
            </td>
            <td class="py-6 text-sm uppercase">
              <div class="flex justify-center">
                <input
                  type="checkbox"
                  class="blue"
                  :id="`include-${property.id}`"
                  v-model="selectedProperties"
                  :value="property"
                >
                <label :for="`include-${property.id}`"></label>
              </div>
            </td>
          </tr>
          <tr v-if="currentPage < totalPages">
            <td class="text-sm py-3 text-center" colspan="8">
              <loader v-if="loadingMore" />
              <button v-else @click="loadMore" class="underline">Load More</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import api from '@/http'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'PropertySelector',

  props: ['value'],

  data () {
    return {
      filtering: {
        searchTerm: '',
        propertyType: 0,
        min_bedrooms: null,
        department: 'RS',
        min_price: null,
        max_price: null
      },
      properties: [],
      loading: false,
      loadingMore: false,
      currentPage: 1,
      totalPages: 1
    }
  },

  created () {
    this.fetchPropertyEnums()
    this.fetchProperties()
  },

  computed: {
    ...mapState(['selectedOffice', 'propertyEnums']),

    prices () {
      let prices = []
      let value = this.filtering.department === 'RS' ? 100000 : 150
      for (let i = 1; i <= 30; i++) {
        prices.push(i * value)
      }

      return prices
    },

    selectedProperties: {
      get () {
        return this.value
      },

      set (values) {
        this.$emit('input', values)
      }
    }
  },

  methods: {
    ...mapActions(['fetchPropertyEnums']),
    fetchProperties (append) {
      if (!append) {
        this.loading = true
      }

      api.get('properties', {
        params: {
          q: this.filtering.searchTerm,
          type: this.filtering.propertyType,
          department: this.filtering.department,
          office_id: this.selectedOffice.id,
          min_price: this.filtering.min_price,
          max_price: this.filtering.max_price,
          min_bedrooms: this.filtering.min_bedrooms,
          order_attribute: 'price',
          order_direction: 'desc',
          page: this.currentPage
        }
      }).then(({ data }) => {
        this.currentPage = data.meta.current_page
        this.totalPages = data.meta.last_page
        let mapped = data.data.map(p => {
          let [date, time] = p.uploaded_at.split(' ')
          p.uploaded_at_date_time = { date, time }
          return p
        })

        if (append) {
          this.properties.push(...mapped)
        } else {
          this.properties = mapped
        }
      }).finally(() => {
        this.loading = false
        this.loadingMore = false
      })
    },

    loadMore () {
      this.loadingMore = true
      this.currentPage++
      this.fetchProperties(true)
    }
  },

  watch: {
    filtering: {
      deep: true,
      handler: function () {
        this.fetchProperties()
      }
    }
  }
}
</script>
